import type { Ref } from 'vue';
import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useToast } from '@getaccept/lib-shared-new/src/toast';
import { t, userLanguage } from '../../lang';
import { SignupService } from '../../api/signup/services/signup.service';
import type { AfterSign } from '../../signing/types/after-sign';
import { AfterSignStep } from '../../signing/types/enums/after-sign-step';
import type { SignupResponse } from '../../signing/types/signup-response';

export const useSignupStore = defineStore('signup', () => {
  const afterSign: Ref<AfterSign> = ref({
    step: AfterSignStep.Init,
    error: '',
    redirectUrl: '',
    didYouMean: '',
    loading: false,
  });

  const createSignup = async (email: string, campaign: string) => {
    afterSign.value.loading = true;
    try {
      const response: SignupResponse = await SignupService.signup(email, campaign, userLanguage());
      if (response?.redirectUrl) {
        afterSign.value = {
          ...afterSign.value,
          redirectUrl: response.redirectUrl,
          error: '',
          didYouMean: '',
          step: AfterSignStep.SignupComplete,
        };
        afterSign.value.step = AfterSignStep.SignupComplete;
      } else if (response && response.error) {
        afterSign.value = {
          ...afterSign.value,
          error: response.error,
          didYouMean: response.didYouMean,
        };
        useToast().toast.danger(t('could-not-create-account'));
      }
    } catch {
      useToast().toast.danger(t('could-not-create-account'));
    } finally {
      afterSign.value.loading = false;
    }
  };

  const sendFeedback = async (feedback: string, email: string) => {
    try {
      await SignupService.feedback(feedback, email);
    } catch {
      useToast().toast.danger(t('could-not-send-feedback'));
    }
  };

  const setAfterSignStep = (step: AfterSignStep) => {
    afterSign.value.step = step;
  };

  return {
    createSignup,
    sendFeedback,
    setAfterSignStep,
    afterSign,
  };
});
